import { Button } from "@mantine/core";
import { IconBrandFacebook, IconBrandGoogle, IconBrandX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import type { MessageKey } from "../../../shared/i18n";
import type { SocialProviderId } from "../../../shared/social";
import { authConfigQuery } from "../../api/queries";
import { authClient } from "../../auth/client";
import { useMessages } from "../../i18n/provider";
import styles from "./auth.module.css";

const LABEL_KEYS = {
  google: "auth.google",
  facebook: "auth.facebook",
  twitter: "auth.x",
} satisfies Record<SocialProviderId, MessageKey>;

const ICONS: Record<SocialProviderId, typeof IconBrandGoogle> = {
  google: IconBrandGoogle,
  facebook: IconBrandFacebook,
  twitter: IconBrandX,
};

export function SocialButtons({ next }: { next: string }) {
  const t = useMessages();
  const { data } = useQuery(authConfigQuery);
  const [pending, setPending] = useState<SocialProviderId | null>(null);
  const [error, setError] = useState("");
  const providers = data?.socialProviders ?? [];

  if (providers.length === 0) return null;

  const signIn = async (provider: SocialProviderId) => {
    setError("");
    setPending(provider);
    const result = await authClient.signIn.social({ provider, callbackURL: next });
    if (result.error) {
      setError(t("auth.socialError"));
      setPending(null);
    }
  };

  return (
    <>
      <div className={styles.form}>
        {providers.map((provider) => {
          const Icon = ICONS[provider];
          return (
            <Button
              key={provider}
              variant="default"
              fullWidth
              leftSection={<Icon size={18} stroke={1.6} aria-hidden />}
              loading={pending === provider}
              disabled={pending !== null && pending !== provider}
              onClick={() => void signIn(provider)}
            >
              {t(LABEL_KEYS[provider])}
            </Button>
          );
        })}
      </div>
      {error ? (
        <div className={styles.error} role="alert" style={{ marginTop: 16 }}>
          {error}
        </div>
      ) : null}
      <div className={styles.divider}>{t("auth.or")}</div>
    </>
  );
}
